import React, { useState, useEffect, useRef } from 'react';
import { mockData } from '../mock';
import { GothicArch, MonasterySeal, OrnamentalBracket, DecorativeLine, MonasteryMark, MonasteryIllustration, MonasterySilhouette } from '../components/MonasteryElements';
import MonasticChat from '../components/MonasticChat';

const HomeUltimate = () => {
  const [scrolled, setScrolled] = useState(false);
  const [currentYear] = useState(new Date().getFullYear());
  const [email, setEmail] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Aggressive badge removal with MutationObserver
    const removeBadge = () => {
      const selectors = [
        '#emergent-badge',
        'a[href*="emergent"]',
        'a[id*="emergent"]',
        'div[id*="emergent"]',
        'iframe[src*="emergent"]',
        '[class*="emergent"]',
        'a[style*="position: fixed"]',
        'a[style*="bottom: 20px"]',
        'img[src*="avatars.githubusercontent"]'
      ];
      
      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          if (el && el.id !== 'root' && !el.closest('#root')) {
            el.remove();
          }
        });
      });
      
      // Also check body children for fixed elements
      document.body.childNodes.forEach(child => {
        if (child.nodeType === 1 && child.id !== 'root' && child.tagName === 'A') {
          child.remove();
        }
      });
    };
    
    // Run immediately
    removeBadge();
    
    // Setup MutationObserver to watch for new elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(() => {
        removeBadge();
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Also run on interval as backup
    const interval = setInterval(removeBadge, 100);
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
      
      // Scroll reveal animation
      const reveals = document.querySelectorAll('.scroll-reveal');
      reveals.forEach(element => {
        const elementTop = element.getBoundingClientRect().top;
        const elementVisible = 150;
        if (elementTop < window.innerHeight - elementVisible) {
          element.classList.add('visible');
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(interval);
      observer.disconnect();
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    console.log('Newsletter signup:', email);
    setEmail('');
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-1000 ${
        scrolled ? 'bg-black/98 backdrop-blur-xl' : 'bg-transparent'
      }`}>
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-8 lg:px-16">
          <div className="flex justify-between items-center py-8 lg:py-10">
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} 
              className="flex items-center space-x-3 hover:opacity-60 transition-opacity"
            >
              <MonasterySeal className="w-10 h-10 text-white" />
              <span className="text-xs tracking-[0.28em] font-light uppercase text-white">Kloster Laks</span>
            </button>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-12 lg:space-x-16">
              {mockData.navigation.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-[11px] tracking-[0.22em] text-gray-400 hover:text-white transition-colors duration-500 uppercase font-light"
                >
                  {item.label}
                </button>
              ))}
            </div>
            
            {/* Desktop CTA */}
            <button 
              onClick={() => scrollToSection('contact')}
              className="hidden md:block text-xs tracking-[0.28em] uppercase font-light border border-white/25 px-6 lg:px-8 py-3 hover:bg-white hover:text-black transition-all duration-500"
            >
              Inquire
            </button>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black border-t border-white/8">
            <div className="px-6 py-8 space-y-6">
              {mockData.navigation.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    scrollToSection(item.id);
                    setMobileMenuOpen(false);
                  }}
                  className="block text-sm tracking-[0.25em] text-gray-400 hover:text-white transition-colors uppercase font-light"
                >
                  {item.label}
                </button>
              ))}
              <button 
                onClick={() => {
                  scrollToSection('contact');
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-xs tracking-[0.28em] uppercase font-light border border-white/25 px-6 py-3 hover:bg-white hover:text-black transition-all duration-500 text-center"
              >
                Inquire
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Enhanced */}
      <section id="hero" className="relative flex items-center justify-center grain" style={{ height: '100vh' }}>
        <div className="absolute inset-0">
          <img 
            src="/images/hero.png"
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.35) contrast(1.12) saturate(0.95)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/70"></div>
        </div>
        <div className="relative z-10 text-center px-6 sm:px-8 lg:px-12">
          <div className="mb-16 md:mb-20 animate-fade-in">
            <DecorativeLine className="w-24 md:w-32 h-4 mx-auto text-white mb-8" />
          </div>
          <h1 className="text-[14vw] sm:text-[10vw] md:text-[8vw] lg:text-[7.5vw] font-light mb-6 md:mb-8 leading-none tracking-tight animate-fade-in" style={{ animationDelay: '0.2s' }}>
            Kloster Laks
          </h1>
          <MonasteryMark className="w-6 h-6 mx-auto mb-4 text-white animate-fade-in" style={{ animationDelay: '0.3s' }} />
          <p className="text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.35em] text-gray-300 uppercase font-light mb-2 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            Monastic Salmon
          </p>
          <p className="text-sm sm:text-base text-gray-300 font-light mb-4 max-w-md mx-auto animate-fade-in leading-relaxed" style={{ animationDelay: '0.5s' }}>
            Handcrafted on Halsnøy Island <MonasteryMark className="w-3 h-3 inline-block mx-1.5 text-white opacity-40" /> Since 2020
          </p>
          <p className="text-sm text-gray-200 font-light mb-16 md:mb-20 animate-fade-in" style={{ animationDelay: '0.6s' }}>
            10 hours <MonasteryMark className="w-3 h-3 inline-block mx-2 text-white opacity-40" /> 25°C <MonasteryMark className="w-3 h-3 inline-block mx-2 text-white opacity-40" /> 100% handmade
          </p>
          <button 
            onClick={() => scrollToSection('story')}
            className="animate-fade-in group"
            style={{ animationDelay: '0.8s' }}
          >
            <div className="flex flex-col items-center">
              <span className="text-[10px] tracking-[0.3em] text-gray-400 uppercase mb-3">Discover</span>
              <div className="w-px h-12 md:h-16 bg-gradient-to-b from-white/50 to-transparent group-hover:h-16 md:group-hover:h-24 transition-all duration-500"></div>
            </div>
          </button>
        </div>
      </section>

      {/* Elite Endorsement - Enhanced */}
      <section className="relative" style={{ paddingTop: '14vh', paddingBottom: '14vh' }}>
        <div className="max-w-screen-xl mx-auto px-6 sm:px-8 lg:px-16">
          <div className="text-center max-w-4xl mx-auto scroll-reveal">
            <p className="text-[10px] sm:text-xs tracking-[0.35em] sm:tracking-[0.38em] text-gray-400 uppercase mb-10 sm:mb-12 font-light">Preferred provider of smoked salmon for</p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-10 sm:gap-16 lg:gap-24">
              <div className="text-center group">
                <div className="mb-4">
                  <MonasteryMark className="w-6 h-6 mx-auto text-white opacity-20" />
                </div>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-light mb-3 text-white tracking-tight group-hover:text-gray-300 transition-colors">Manchester City FC</p>
                <p className="text-[10px] sm:text-xs tracking-[0.22em] sm:tracking-[0.25em] text-gray-500 uppercase mb-3">Official Supplier</p>
                <p className="text-xs text-gray-600 font-light italic max-w-xs mx-auto">Fueling champions with Norwegian excellence</p>
              </div>
              <div className="hidden sm:block w-px h-20 lg:h-24 bg-white/10"></div>
              <div className="text-center group">
                <div className="mb-4">
                  <MonasteryMark className="w-6 h-6 mx-auto text-white opacity-20" />
                </div>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-light mb-3 text-white tracking-tight group-hover:text-gray-300 transition-colors">Erling Haaland</p>
                <p className="text-[10px] sm:text-xs tracking-[0.22em] sm:tracking-[0.25em] text-gray-500 uppercase mb-3">Personal Choice</p>
                <p className="text-xs text-gray-600 font-light italic max-w-xs mx-auto">A testament to world-class quality</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statement Section - REIMAGINED with Three Pillars */}
      <section className="relative overflow-hidden" style={{ paddingTop: '30vh', paddingBottom: '30vh' }}>
        {/* Monastery watermark - slightly more visible */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-full max-w-6xl h-full flex items-center justify-center">
            <img 
              src="/images/monastery-featured.png" 
              alt="" 
              className="w-full h-auto object-contain opacity-[0.13]"
              style={{ 
                filter: 'grayscale(1) brightness(1.6) contrast(0.85)',
                maskImage: 'radial-gradient(ellipse 80% 60% at center, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0) 75%)',
                WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at center, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0) 75%)'
              }}
            />
          </div>
        </div>
        
        {/* Content */}
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-8 lg:px-16 relative z-10">
          {/* Gothic arch at top */}
          <div className="mb-24 sm:mb-28">
            <GothicArch className="w-48 sm:w-56 h-12 mx-auto text-white opacity-30" />
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="scroll-reveal text-center">
              {/* Header */}
              <div className="flex items-center justify-center gap-5 mb-20">
                <OrnamentalBracket className="w-6 h-14 text-white opacity-35" side="left" />
                <div className="flex items-center gap-3">
                  <MonasteryMark className="w-4 h-4 text-white opacity-30" />
                  <p className="text-sm tracking-[0.4em] text-gray-300 uppercase font-light">The Monastic Way</p>
                  <MonasteryMark className="w-4 h-4 text-white opacity-30" />
                </div>
                <OrnamentalBracket className="w-6 h-14 text-white opacity-35" side="right" />
              </div>
              
              {/* Three Pillars Layout */}
              <div className="grid md:grid-cols-3 gap-16 sm:gap-20 lg:gap-24 mb-32 sm:mb-36">
                <div className="group">
                  <div className="mb-8">
                    <p className="text-8xl sm:text-9xl font-light text-white/8 stat-number group-hover:text-white/15 transition-colors duration-700">10</p>
                  </div>
                  <h3 className="text-3xl sm:text-4xl font-light text-white mb-6 tracking-tight leading-tight">Hours of<br />Patience</h3>
                  <DecorativeLine className="w-16 h-3 mx-auto mb-6 text-white opacity-25" />
                  <p className="text-sm sm:text-base text-gray-300 font-light leading-[1.85]">
                    Not a minute less. Time cannot be rushed in the pursuit of perfection.
                  </p>
                </div>
                
                <div className="group">
                  <div className="mb-8">
                    <p className="text-8xl sm:text-9xl font-light text-white/8 stat-number group-hover:text-white/15 transition-colors duration-700">25°</p>
                  </div>
                  <h3 className="text-3xl sm:text-4xl font-light text-white mb-6 tracking-tight leading-tight">Degrees of<br />Precision</h3>
                  <DecorativeLine className="w-16 h-3 mx-auto mb-6 text-white opacity-25" />
                  <p className="text-sm sm:text-base text-gray-300 font-light leading-[1.85]">
                    Cold smoking that whispers, never shouts. Temperature mastery refined.
                  </p>
                </div>
                
                <div className="group">
                  <div className="mb-8">
                    <p className="text-8xl sm:text-9xl font-light text-white/8 stat-number group-hover:text-white/15 transition-colors duration-700">100%</p>
                  </div>
                  <h3 className="text-3xl sm:text-4xl font-light text-white mb-6 tracking-tight leading-tight">Human<br />Dedication</h3>
                  <DecorativeLine className="w-16 h-3 mx-auto mb-6 text-white opacity-25" />
                  <p className="text-sm sm:text-base text-gray-300 font-light leading-[1.85]">
                    Every fillet touched by hands. Machines cannot discern excellence.
                  </p>
                </div>
              </div>
              
              {/* Monastery Statement */}
              <div className="max-w-4xl mx-auto border-t border-white/5 pt-16 sm:pt-20">
                <p className="text-2xl sm:text-3xl md:text-4xl text-gray-50 font-light leading-[1.65] italic mb-10">
                  On the west coast of Norway, we honor the ancient Augustinian tradition.
                </p>
                
                <p className="text-lg sm:text-xl text-gray-300 font-light leading-[1.9]">
                  Where time, temperature, and human touch transform the finest fillets into something extraordinary.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Heritage Section - Enhanced with Vector Monastery */}
      <section id="story" className="relative" style={{ paddingTop: '20vh', paddingBottom: '24vh' }}>
        {/* Subtle Monastery Silhouette Background */}
        <div className="absolute left-0 lg:left-10 top-1/2 -translate-y-1/2 w-full lg:w-1/2 h-full flex items-center justify-start pointer-events-none">
          <MonasterySilhouette className="w-full max-w-2xl h-auto text-white" />
        </div>
        
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-8 lg:px-16 relative z-10">
          <div className="grid lg:grid-cols-12 gap-20 lg:gap-32">
            <div className="lg:col-span-5 scroll-reveal">
              <div className="lg:sticky lg:top-32">
                <div className="flex items-center justify-center lg:justify-start gap-3 mb-12">
                  <OrnamentalBracket className="w-4 h-8 text-white" side="left" />
                  <p className="text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.3em] text-gray-500 uppercase font-light">Heritage</p>
                  <OrnamentalBracket className="w-4 h-8 text-white" side="right" />
                </div>
                <h2 className="text-[2.75rem] sm:text-[3.75rem] md:text-[4.75rem] lg:text-[5.5rem] font-light mb-8 leading-[0.95]">
                  Rooted in<br />Tradition
                </h2>
                <DecorativeLine className="w-20 h-4 mb-12 text-white" />
                <div className="space-y-8 text-base sm:text-[17px] text-gray-200 font-light leading-[1.9]">
                  {mockData.story.paragraphs.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
                <div className="mt-16 pt-10 border-t border-white/5">
                  <p className="text-sm sm:text-base text-gray-300 mb-2">
                    <span className="font-light">10th Century Monastic Tradition</span>
                  </p>
                  <p className="text-xs sm:text-sm text-gray-400">
                    Established 2020 • Halsnøy Island, Norway
                  </p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 scroll-reveal">
              <div className="image-container relative group hover-glow mb-16" style={{ aspectRatio: '3/4' }}>
                <div className="absolute inset-0 border border-white/5 pointer-events-none z-10"></div>
                <img 
                  src="/images/heritage.png"
                  alt="Halsnøy"
                  className="w-full h-full object-cover"
                  style={{
                    maskImage: `
                      linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 5%, rgba(0,0,0,0.7) 12%, rgba(0,0,0,1) 20%),
                      linear-gradient(to left, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 5%, rgba(0,0,0,0.7) 10%, rgba(0,0,0,1) 18%),
                      linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 5%, rgba(0,0,0,0.8) 10%, rgba(0,0,0,1) 18%),
                      linear-gradient(to top, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 5%, rgba(0,0,0,0.8) 10%, rgba(0,0,0,1) 18%),
                      radial-gradient(ellipse 110% 105% at center, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 40%, rgba(0,0,0,0.96) 60%, rgba(0,0,0,0.85) 72%, rgba(0,0,0,0.6) 82%, rgba(0,0,0,0) 95%)
                    `,
                    WebkitMaskImage: `
                      linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 5%, rgba(0,0,0,0.7) 12%, rgba(0,0,0,1) 20%),
                      linear-gradient(to left, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 5%, rgba(0,0,0,0.7) 10%, rgba(0,0,0,1) 18%),
                      linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 5%, rgba(0,0,0,0.8) 10%, rgba(0,0,0,1) 18%),
                      linear-gradient(to top, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 5%, rgba(0,0,0,0.8) 10%, rgba(0,0,0,1) 18%),
                      radial-gradient(ellipse 110% 105% at center, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 40%, rgba(0,0,0,0.96) 60%, rgba(0,0,0,0.85) 72%, rgba(0,0,0,0.6) 82%, rgba(0,0,0,0) 95%)
                    `,
                    maskComposite: 'intersect',
                    WebkitMaskComposite: 'source-in'
                  }}
                />
                <div className="absolute bottom-8 left-8 glass-card px-6 py-4">
                  <p className="text-xs text-white tracking-[0.2em] uppercase">Halsnøy, Norway</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-10 sm:gap-12 lg:gap-16">
                <div className="text-center group cursor-default">
                  <p className="text-5xl sm:text-6xl font-light mb-4 stat-number text-white group-hover:text-gray-300 transition-colors duration-500">10</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-[0.25em] font-light">Hours</p>
                </div>
                <div className="text-center group cursor-default">
                  <p className="text-5xl sm:text-6xl font-light mb-4 stat-number text-white group-hover:text-gray-300 transition-colors duration-500">25°</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-[0.25em] font-light">Maximum</p>
                </div>
                <div className="text-center group cursor-default">
                  <p className="text-5xl sm:text-6xl font-light mb-4 stat-number text-white group-hover:text-gray-300 transition-colors duration-500">100%</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-[0.25em] font-light">Handmade</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section - With Salmon Watermark */}
      <section id="process" className="relative border-t border-white/5" style={{ paddingTop: '26vh', paddingBottom: '26vh' }}>
        {/* Transition Divider */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <GothicArch className="w-48 h-10 text-white opacity-25" />
        </div>
        
        {/* Salmon illustration watermark - slightly more visible */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-full max-w-6xl h-full flex items-center justify-center">
            <img 
              src="/images/salmon-illustration.png" 
              alt="" 
              className="w-full h-auto object-contain opacity-[0.13]"
              style={{ 
                filter: 'grayscale(1) brightness(1.5) contrast(0.9)',
                maskImage: 'radial-gradient(ellipse 75% 55% at center, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 45%, rgba(0,0,0,0) 70%)',
                WebkitMaskImage: 'radial-gradient(ellipse 75% 55% at center, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 45%, rgba(0,0,0,0) 70%)'
              }}
            />
          </div>
        </div>
        
        <div className="max-w-screen-xl mx-auto px-6 sm:px-8 lg:px-16 relative z-10">
          <div className="mb-32 sm:mb-36 scroll-reveal">
            <div className="flex items-center justify-center gap-3 mb-12">
              <OrnamentalBracket className="w-4 h-8 text-white" side="left" />
              <p className="text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.3em] text-gray-500 uppercase font-light">Method</p>
              <OrnamentalBracket className="w-4 h-8 text-white" side="right" />
            </div>
            <h2 className="text-[2.5rem] sm:text-[3.5rem] md:text-[4.5rem] lg:text-[5rem] font-light leading-[0.98] mb-8 text-center">Four Stages</h2>
            <DecorativeLine className="w-24 sm:w-32 h-4 mx-auto mb-8 text-white" />
            <p className="text-base text-gray-400 font-light max-w-2xl leading-relaxed mx-auto text-center">
              Each stage is a meditation. A commitment to quality that cannot be rushed.
            </p>
          </div>
          <div className="space-y-24 sm:space-y-28 lg:space-y-32">
            {mockData.process.map((step, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 lg:gap-12 scroll-reveal">
                <div className="md:col-span-2 hidden md:block">
                  <p className="text-7xl lg:text-8xl font-light text-white/6 stat-number">{String(index + 1).padStart(2, '0')}</p>
                </div>
                <div className="md:col-span-10 grid md:grid-cols-3 gap-6 md:gap-10 lg:gap-12">
                  <div className="md:col-span-1">
                    <p className="text-xs text-gray-500 uppercase tracking-[0.25em] mb-4 md:hidden">{String(index + 1).padStart(2, '0')}</p>
                    <h3 className="text-2xl sm:text-3xl font-light leading-tight text-white">{step.title}</h3>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm sm:text-base text-gray-200 font-light leading-[1.85]">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Signature Gallery - Enhanced Presentation */}
      <section className="relative" style={{ paddingTop: '24vh', paddingBottom: '24vh' }}>
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-8 lg:px-16">
          <div className="mb-32 sm:mb-36 text-center scroll-reveal">
            <div className="flex items-center justify-center gap-3 mb-10">
              <OrnamentalBracket className="w-5 h-10 text-white opacity-50" side="left" />
              <p className="text-xs tracking-[0.3em] text-gray-300 uppercase font-light">Signature Presentations</p>
              <OrnamentalBracket className="w-5 h-10 text-white opacity-50" side="right" />
            </div>
            <h2 className="text-[3rem] sm:text-[4rem] md:text-[5rem] font-light leading-[0.98] mb-10">Artistry in Every Plate</h2>
            <DecorativeLine className="w-32 h-5 mx-auto mb-10 text-white opacity-50" />
            <p className="text-base sm:text-lg text-gray-100 font-light max-w-3xl mx-auto leading-[1.9]">
              Each presentation is a canvas. Every garnish placed with intention. Gourmet excellence refined through monastic devotion.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 sm:gap-12 lg:gap-16 scroll-reveal">
            {/* Showcase 1 */}
            <div className="group">
              <div className="relative bg-gradient-to-b from-white/[0.02] to-transparent border-2 border-white/10 hover:border-white/20 transition-all duration-700 hover-glow p-10 sm:p-12 lg:p-16 overflow-hidden" style={{ minHeight: '550px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img 
                  src="/images/showcase-1.png"
                  alt="Signature Presentation"
                  className="max-w-full h-auto object-contain group-hover:scale-105 transition-transform duration-700"
                  style={{ maxHeight: '650px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              </div>
              <div className="mt-7 text-center">
                <p className="text-base sm:text-lg font-light text-white mb-2">Signature Presentation</p>
                <p className="text-xs text-gray-500 tracking-wide">Classic elegance</p>
              </div>
            </div>
            
            {/* Showcase 2 */}
            <div className="group">
              <div className="relative bg-gradient-to-b from-white/[0.02] to-transparent border-2 border-white/10 hover:border-white/20 transition-all duration-700 hover-glow p-10 sm:p-12 lg:p-16 overflow-hidden" style={{ minHeight: '550px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img 
                  src="/images/showcase-2.png"
                  alt="Artisan Selection"
                  className="max-w-full h-auto object-contain group-hover:scale-105 transition-transform duration-700"
                  style={{ maxHeight: '650px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              </div>
              <div className="mt-7 text-center">
                <p className="text-base sm:text-lg font-light text-white mb-2">Artisan Selection</p>
                <p className="text-xs text-gray-500 tracking-wide">Botanical artistry</p>
              </div>
            </div>
            
            {/* Showcase 3 */}
            <div className="group">
              <div className="relative bg-gradient-to-b from-white/[0.02] to-transparent border-2 border-white/10 hover:border-white/20 transition-all duration-700 hover-glow p-10 sm:p-12 lg:p-16 overflow-hidden" style={{ minHeight: '550px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img 
                  src="/images/showcase-3.png"
                  alt="Gourmet Plating"
                  className="max-w-full h-auto object-contain group-hover:scale-105 transition-transform duration-700"
                  style={{ maxHeight: '650px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              </div>
              <div className="mt-7 text-center">
                <p className="text-base sm:text-lg font-light text-white mb-2">Gourmet Plating</p>
                <p className="text-xs text-gray-500 tracking-wide">Edible florals</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Collection Section - Enhanced */}
      <section id="flavours" className="relative border-t border-white/5" style={{ paddingTop: '26vh', paddingBottom: '26vh' }}>
        {/* Transition Divider */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <DecorativeLine className="w-56 h-6 text-white opacity-30" />
        </div>
        
        <div className="max-w-screen-xl mx-auto px-6 sm:px-8 lg:px-16">
          <div className="mb-32 sm:mb-36 scroll-reveal">
            <div className="flex items-center justify-center gap-3 mb-12">
              <OrnamentalBracket className="w-4 h-8 text-white" side="left" />
              <p className="text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.3em] text-gray-500 uppercase font-light">Varieties</p>
              <OrnamentalBracket className="w-4 h-8 text-white" side="right" />
            </div>
            <h2 className="text-[2.5rem] sm:text-[3.5rem] md:text-[4.5rem] lg:text-[5rem] font-light leading-[0.98] mb-8 text-center">The Collection</h2>
            <DecorativeLine className="w-24 sm:w-32 h-4 mx-auto mb-8 text-white" />
            <p className="text-base sm:text-lg text-gray-400 font-light max-w-2xl leading-relaxed mx-auto text-center">
              Four distinctive expressions, each a testament to Norwegian craftsmanship and flavor mastery.
            </p>
          </div>
          <div className="space-y-32 sm:space-y-36 lg:space-y-40">
            {mockData.flavours.map((flavour, index) => {
              const productImages = [
                '/images/product-1.png',
                '/images/product-2.png',
                '/images/product-3.png',
                '/images/product-4.png'
              ];
              const isEven = index % 2 === 0;
              return (
                <div key={index} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-16 items-center scroll-reveal`}>
                  <div className={`lg:col-span-7 image-container relative group hover-glow ${isEven ? 'lg:order-1' : 'lg:order-2'}`} style={{ aspectRatio: '4/3' }}>
                    <div className="absolute inset-0 border border-white/5 pointer-events-none z-10"></div>
                    <img 
                      src={productImages[index]}
                      alt={flavour.name}
                      className="w-full h-full object-cover"
                      style={{
                        maskImage: `
                          linear-gradient(to ${isEven ? 'left' : 'right'}, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 5%, rgba(0,0,0,0.7) 12%, rgba(0,0,0,1) 20%),
                          linear-gradient(to ${isEven ? 'right' : 'left'}, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 5%, rgba(0,0,0,0.7) 10%, rgba(0,0,0,1) 18%),
                          linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 5%, rgba(0,0,0,0.8) 10%, rgba(0,0,0,1) 18%),
                          linear-gradient(to top, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 5%, rgba(0,0,0,0.8) 10%, rgba(0,0,0,1) 18%),
                          radial-gradient(ellipse 110% 105% at ${isEven ? '40%' : '60%'} 50%, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 40%, rgba(0,0,0,0.96) 60%, rgba(0,0,0,0.85) 72%, rgba(0,0,0,0.6) 82%, rgba(0,0,0,0) 95%)
                        `,
                        WebkitMaskImage: `
                          linear-gradient(to ${isEven ? 'left' : 'right'}, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 5%, rgba(0,0,0,0.7) 12%, rgba(0,0,0,1) 20%),
                          linear-gradient(to ${isEven ? 'right' : 'left'}, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 5%, rgba(0,0,0,0.7) 10%, rgba(0,0,0,1) 18%),
                          linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 5%, rgba(0,0,0,0.8) 10%, rgba(0,0,0,1) 18%),
                          linear-gradient(to top, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 5%, rgba(0,0,0,0.8) 10%, rgba(0,0,0,1) 18%),
                          radial-gradient(ellipse 110% 105% at ${isEven ? '40%' : '60%'} 50%, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 40%, rgba(0,0,0,0.96) 60%, rgba(0,0,0,0.85) 72%, rgba(0,0,0,0.6) 82%, rgba(0,0,0,0) 95%)
                        `,
                        maskComposite: 'intersect',
                        WebkitMaskComposite: 'source-in'
                      }}
                    />
                    <div className="absolute top-6 sm:top-8 right-6 sm:right-8 glass-card px-4 sm:px-5 py-2 sm:py-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <p className="text-[10px] sm:text-xs text-white tracking-[0.22em] sm:tracking-[0.25em]">{String(index + 1).padStart(2, '0')}</p>
                    </div>
                  </div>
                  <div className={`lg:col-span-5 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                    <p className="text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.3em] text-gray-400 uppercase mb-6 sm:mb-8 font-light stat-number">{String(index + 1).padStart(2, '0')}</p>
                    <h3 className="text-[2rem] sm:text-[2.75rem] md:text-[3.25rem] lg:text-[3.5rem] font-light mb-6 sm:mb-8 leading-tight">{flavour.name}</h3>
                    <p className="text-sm sm:text-base text-gray-200 font-light leading-[1.85] mb-6 sm:mb-8">{flavour.description}</p>
                    <p className="text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.22em] text-gray-400 uppercase font-light">{flavour.notes}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="relative" style={{ paddingTop: '24vh', paddingBottom: '24vh' }}>
        <div className="max-w-screen-xl mx-auto px-6 sm:px-8 lg:px-16">
          <div className="mb-24 sm:mb-28 text-center scroll-reveal">
            <div className="flex items-center justify-center gap-3 mb-6">
              <OrnamentalBracket className="w-4 h-8 text-white" side="left" />
              <p className="text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.3em] text-gray-400 uppercase font-light">What They Say</p>
              <OrnamentalBracket className="w-4 h-8 text-white" side="right" />
            </div>
            <h2 className="text-[2.75rem] sm:text-[3.75rem] md:text-[4.25rem] font-light leading-[0.98]">Trusted Excellence</h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-16 sm:gap-20 lg:gap-24">
            {mockData.testimonials.map((testimonial, index) => (
              <div key={index} className="scroll-reveal hover:opacity-90 transition-opacity duration-500">
                <div className="mb-10 sm:mb-12">
                  <div className="flex gap-1.5 mb-8 sm:mb-10 justify-center">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="w-1.5 h-1.5 bg-white/50 rounded-full"></div>
                    ))}
                  </div>
                  <p className="text-base sm:text-lg font-light leading-[1.75] sm:leading-[1.8] text-gray-100 text-center">
                    "{testimonial.quote}"
                  </p>
                </div>
                <div className="mt-8 sm:mt-10 text-center">
                  <DecorativeLine className="w-16 h-3 mx-auto mb-5 text-white" />
                  <p className="text-sm sm:text-base font-light mb-2 text-white">{testimonial.name}</p>
                  <p className="text-xs text-gray-400 tracking-wide">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative border-t border-white/8" style={{ paddingTop: '20vh', paddingBottom: '20vh' }}>
        <div className="max-w-screen-xl mx-auto px-6 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 sm:gap-18 lg:gap-20">
            <div className="lg:col-span-5 scroll-reveal">
              <p className="text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.3em] text-gray-500 uppercase mb-10 sm:mb-12 font-light">Inquiry</p>
              <h2 className="text-[2.75rem] sm:text-[3.5rem] md:text-[4.5rem] font-light mb-20 sm:mb-24 leading-[0.98]">Contact</h2>
              <div className="space-y-12 sm:space-y-14 lg:space-y-16">
                {mockData.contacts.map((contact, index) => (
                  <div key={index}>
                    <p className="text-lg sm:text-xl font-light mb-3 text-white">{contact.name}</p>
                    <p className="text-[10px] sm:text-[11px] text-gray-500 uppercase tracking-[0.2em] sm:tracking-[0.22em] mb-5 sm:mb-6 font-light">{contact.role}</p>
                    <div className="space-y-2">
                      <a href={`tel:${contact.phone}`} className="block text-sm text-gray-400 hover:text-white transition-colors">{contact.phone}</a>
                      <a href={`mailto:${contact.email}`} className="block text-sm text-gray-400 hover:text-white transition-colors">{contact.email}</a>
                    </div>
                  </div>
                ))}
                <div className="pt-6 sm:pt-8">
                  <p className="text-sm text-gray-500 mb-1">Halsnøy, Norway</p>
                  <p className="text-xs text-gray-600">West Coast</p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 scroll-reveal">
              <form className="space-y-10 sm:space-y-12 pt-12 sm:pt-20 lg:pt-24">
                <div>
                  <label className="text-[10px] tracking-[0.25em] text-gray-500 uppercase mb-4 block">Name</label>
                  <input
                    type="text"
                    className="w-full px-0 py-5 sm:py-6 bg-transparent border-b border-white/15 text-white focus:border-white transition-colors duration-500 text-sm sm:text-base font-light placeholder-gray-600"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] text-gray-500 uppercase mb-4 block">Email</label>
                  <input
                    type="email"
                    className="w-full px-0 py-5 sm:py-6 bg-transparent border-b border-white/15 text-white focus:border-white transition-colors duration-500 text-sm sm:text-base font-light placeholder-gray-600"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] text-gray-500 uppercase mb-4 block">Message</label>
                  <textarea
                    rows="3"
                    className="w-full px-0 py-5 sm:py-6 bg-transparent border-b border-white/15 text-white focus:border-white transition-colors duration-500 text-sm sm:text-base font-light resize-none placeholder-gray-600"
                    placeholder="Tell us about your inquiry..."
                  ></textarea>
                </div>
                <div className="pt-6 sm:pt-8">
                  <button 
                    type="submit" 
                    className="w-full sm:w-auto text-xs tracking-[0.3em] uppercase font-light border border-white/25 px-12 sm:px-16 py-5 sm:py-6 hover:bg-white hover:text-black transition-all duration-500"
                  >
                    Send Inquiry
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section - Enhanced with Monastery Theme */}
      <section className="relative" style={{ paddingTop: '22vh', paddingBottom: '22vh' }}>
        <div className="max-w-screen-xl mx-auto px-6 sm:px-8 lg:px-16">
          <div className="max-w-4xl mx-auto scroll-reveal">
            <div className="text-center mb-20 sm:mb-24">
              <div className="flex items-center justify-center gap-3 mb-8">
                <OrnamentalBracket className="w-4 h-8 text-white" side="left" />
                <p className="text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.3em] text-gray-400 uppercase font-light">Philosophy</p>
                <OrnamentalBracket className="w-4 h-8 text-white" side="right" />
              </div>
              <h2 className="text-[2.75rem] sm:text-[3.75rem] md:text-[4.75rem] font-light leading-[0.98] mb-6">Monastic Values</h2>
              <DecorativeLine className="w-24 sm:w-32 h-4 mx-auto text-white" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-12 sm:gap-14 lg:gap-16">
              <div className="text-center sm:text-left group">
                <div className="w-12 h-px bg-white/20 mb-6 mx-auto sm:mx-0 group-hover:w-16 transition-all duration-500"></div>
                <h3 className="text-xl sm:text-2xl font-light mb-4 text-white">Patience</h3>
                <p className="text-sm sm:text-base text-gray-200 font-light leading-[1.85]">
                  We refuse shortcuts. Ten hours minimum for every batch, following 10th century monastic traditions.
                </p>
              </div>
              <div className="text-center sm:text-left group">
                <div className="w-12 h-px bg-white/20 mb-6 mx-auto sm:mx-0 group-hover:w-16 transition-all duration-500"></div>
                <h3 className="text-xl sm:text-2xl font-light mb-4 text-white">Devotion</h3>
                <p className="text-sm sm:text-base text-gray-200 font-light leading-[1.85]">
                  Every fillet is personally inspected and chosen. Only the finest Norwegian salmon earns our name.
                </p>
              </div>
              <div className="text-center sm:text-left group">
                <div className="w-12 h-px bg-white/20 mb-6 mx-auto sm:mx-0 group-hover:w-16 transition-all duration-500"></div>
                <h3 className="text-xl sm:text-2xl font-light mb-4 text-white">Reverence</h3>
                <p className="text-sm sm:text-base text-gray-200 font-light leading-[1.85]">
                  Oak from Norwegian forests, juniper from coastal hills—every ingredient sourced with intention and respect.
                </p>
              </div>
              <div className="text-center sm:text-left group">
                <div className="w-12 h-px bg-white/20 mb-6 mx-auto sm:mx-0 group-hover:w-16 transition-all duration-500"></div>
                <h3 className="text-xl sm:text-2xl font-light mb-4 text-white">Excellence</h3>
                <p className="text-sm sm:text-base text-gray-200 font-light leading-[1.85]">
                  Chosen by Manchester City FC and Erling Haaland. Trusted by world-class athletes and connoisseurs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Monastic Chat Section */}
      <MonasticChat />

      {/* Newsletter Section - Enhanced */}
      <section className="relative border-t border-white/8" style={{ paddingTop: '16vh', paddingBottom: '16vh' }}>
        <div className="max-w-screen-xl mx-auto px-6 sm:px-8 lg:px-16">
          <div className="max-w-3xl mx-auto text-center scroll-reveal">
            <p className="text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.3em] text-gray-500 uppercase mb-8 sm:mb-10 font-light">Newsletter</p>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-light mb-8 sm:mb-10 text-white leading-tight">Stay Informed</h3>
            <p className="text-sm sm:text-base text-gray-400 mb-10 sm:mb-12 font-light leading-[1.75] px-4 sm:px-0">
              Receive updates on new varieties, exclusive offerings, and the art of Norwegian salmon craftsmanship.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto px-4 sm:px-0">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 px-6 sm:px-8 py-4 sm:py-5 bg-transparent border border-white/20 text-white focus:border-white transition-colors duration-500 text-sm font-light placeholder-gray-600 text-center sm:text-left"
                placeholder="your@email.com"
                required
              />
              <button 
                type="submit"
                className="text-xs tracking-[0.28em] uppercase font-light border border-white/25 px-10 sm:px-12 py-4 sm:py-5 hover:bg-white hover:text-black transition-all duration-500 whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/8" style={{ paddingTop: '12vh', paddingBottom: '12vh' }}>
        <div className="max-w-screen-xl mx-auto px-6 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-12 sm:gap-16 mb-16 sm:mb-20">
            <div className="md:col-span-5">
              <p className="text-xs tracking-[0.28em] uppercase font-light mb-6 sm:mb-8">Kloster Laks</p>
              <p className="text-sm sm:text-base text-gray-400 font-light leading-[1.75] sm:leading-relaxed max-w-sm">
                Traditional Norwegian cold-smoked salmon, handcrafted on the island of Halsnøy since 2020.
              </p>
              <div className="mt-8 sm:mt-10">
                <p className="text-xs text-gray-600 mb-2">Preferred by</p>
                <p className="text-sm text-gray-500 font-light">Manchester City FC • Erling Haaland</p>
              </div>
            </div>
            <div className="md:col-span-2">
              <p className="text-[10px] tracking-[0.25em] text-gray-600 uppercase mb-6 font-light">Navigate</p>
              <div className="space-y-3">
                {mockData.navigation.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="block text-sm text-gray-400 hover:text-white transition-colors font-light"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-3">
              <p className="text-[10px] tracking-[0.25em] text-gray-600 uppercase mb-6 font-light">Contact</p>
              <div className="space-y-3">
                <a href={`mailto:${mockData.contacts[0].email}`} className="block text-sm text-gray-400 hover:text-white transition-colors font-light">
                  {mockData.contacts[0].email}
                </a>
                <a href={`tel:${mockData.contacts[0].phone}`} className="block text-sm text-gray-400 hover:text-white transition-colors font-light">
                  {mockData.contacts[0].phone}
                </a>
              </div>
            </div>
            <div className="md:col-span-2">
              <p className="text-[10px] tracking-[0.25em] text-gray-600 uppercase mb-6 font-light">Location</p>
              <p className="text-sm text-gray-500 font-light leading-relaxed">
                Halsnøy<br />
                Norway
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-12 sm:pt-16 mt-12 sm:mt-16 border-t border-white/5">
            <p className="text-xs tracking-[0.28em] uppercase font-light">Kloster Laks</p>
            <p className="text-xs text-gray-600 font-light">© 2020–{currentYear}. Handcrafted in Norway.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomeUltimate;
